import React, { useState, useEffect } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { storage, db } from '@/lib/firebase';
import PageBackground from '@/components/PageBackground';
import MobileBackButton from '@/components/MobileBackButton';

export default function QRCodeManagement() {
  const [qrSettings, setQrSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');

  // Load current QR settings
  useEffect(() => {
    const qrRef = doc(db, 'settings', 'qr');
    const unsubscribe = onSnapshot(qrRef, (doc) => {
      if (doc.exists()) {
        setQrSettings(doc.data());
      }
    });

    return () => unsubscribe();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Reset uploaded URL when new file is selected
      setUploadedUrl('');
    }
  };

  const uploadQRCode = async (type) => {
    const fileInput = document.getElementById(`qr-file-${type}`);
    const file = fileInput?.files[0];
    
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageReference = storageRef(storage, `qr-codes/${type}-qr.png`);
      await uploadBytes(storageReference, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageReference);

      // Update Firestore QR settings
      const qrRef = doc(db, 'settings', 'qr');
      await updateDoc(qrRef, {
        [`static.${type}.imageUrl`]: downloadURL,
        [`static.${type}.label`]: `PPAC ${type.toUpperCase()} QR`,
        updatedAt: serverTimestamp()
      });

      setUploadedUrl(downloadURL);
      alert(`${type.toUpperCase()} QR code uploaded successfully!`);
      
      // Clear file input
      fileInput.value = '';
    } catch (error) {
              // Error uploading QR code
      alert('Error uploading QR code: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const removeQRCode = async (type) => {
    if (!qrSettings?.static?.[type]?.imageUrl) return;

    if (confirm(`Are you sure you want to remove the ${type} QR code?`)) {
      try {
        // Delete from Firebase Storage
        const storageReference = storageRef(storage, `qr-codes/${type}-qr.png`);
        await deleteObject(storageReference);

        // Update Firestore QR settings
        const qrRef = doc(db, 'settings', 'qr');
        await updateDoc(qrRef, {
          [`static.${type}.imageUrl`]: '',
          [`static.${type}.label`]: `PPAC ${type.toUpperCase()} QR`,
          updatedAt: serverTimestamp()
        });

        alert(`${type.toUpperCase()} QR code removed successfully!`);
      } catch (error) {
        // Error removing QR code
        alert('Error removing QR code: ' + error.message);
      }
    }
  };

  return (
          <PageBackground edgeToEdge={true}>
      <div className="page-boxed page-gutter py-4 lg:py-6">
        <div className="flex items-center gap-3 mb-6">
          <MobileBackButton />
          <h1 className="text-2xl sm:text-3xl font-bold">QR Code Management</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GCash QR Code */}
          <div className="card p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              GCash QR Code
            </h2>
            
            <div className="space-y-4">
              {/* Current QR Code Display */}
              {qrSettings?.static?.gcash?.imageUrl ? (
                <div className="text-center">
                  <img 
                    src={qrSettings.static.gcash.imageUrl} 
                    alt="GCash QR Code" 
                    className="max-w-[200px] mx-auto border rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Current GCash QR Code
                  </p>
                  <button
                    onClick={() => removeQRCode('gcash')}
                    className="btn btn-sm btn-outline btn-red mt-2"
                  >
                    Remove QR Code
                  </button>
                </div>
              ) : (
                <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded">
                  <p className="text-gray-500">No GCash QR code uploaded</p>
                </div>
              )}

              {/* Upload Section */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Upload New GCash QR Code
                </label>
                <input
                  id="qr-file-gcash"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 mb-2"
                />
                <button
                  onClick={() => uploadQRCode('gcash')}
                  disabled={uploading}
                  className="w-full btn btn-primary"
                >
                  {uploading ? 'Uploading...' : 'Upload GCash QR Code'}
                </button>
              </div>
            </div>
          </div>

          {/* Bank QR Code */}
          <div className="card p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              Bank QR Code
            </h2>
            
            <div className="space-y-4">
              {/* Current QR Code Display */}
              {qrSettings?.static?.bank?.imageUrl ? (
                <div className="text-center">
                  <img 
                    src={qrSettings.static.bank.imageUrl} 
                    alt="Bank QR Code" 
                    className="max-w-[200px] mx-auto border rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Current Bank QR Code
                  </p>
                  <button
                    onClick={() => removeQRCode('bank')}
                    className="btn btn-sm btn-outline btn-red mt-2"
                  >
                    Remove QR Code
                  </button>
                </div>
              ) : (
                <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded">
                  <p className="text-gray-500">No Bank QR code uploaded</p>
                </div>
              )}

              {/* Upload Section */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Upload New Bank QR Code
                </label>
                <input
                  id="qr-file-bank"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 mb-2"
                />
                <button
                  onClick={() => uploadQRCode('bank')}
                  disabled={uploading}
                  className="w-full btn btn-primary"
                >
                  {uploading ? 'Uploading...' : 'Upload Bank QR Code'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="card p-4 mt-6">
          <h3 className="text-lg font-semibold mb-3">Instructions</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• Upload PNG, JPG, or JPEG images for best quality</p>
            <p>• Recommended size: 200x200 pixels or larger</p>
            <p>• The QR codes will be displayed in checkout and payment forms</p>
            <p>• Users can scan these QR codes to make payments</p>
          </div>
        </div>
      </div>
    </PageBackground>
  );
}
